/**
 * The energy certificate's three required uploads (exterior view, heater nameplate, building shape),
 * recorded in the configuration through `useGatedUploadScope`, plus the optional construction
 * description, recorded the same way so a resumed draft keeps it. Completeness reads the recorded
 * filenames, never the live upload areas.
 */

import type { CartItemId } from 'models/purchaseFlowRevamp'

import {
  useBuildingShapeUpload,
  useConstructionDescUpload,
  useExteriorViewUpload,
  useGatedUploadScope,
  useHeaterNameplateUpload,
  useLineDocScope,
  useSessionDocumentsDropped
} from '../../fileHub'
import { usePfStore } from '../../store'
import { useEnergyCertificateConfig } from './useEnergyCertificateConfig'

export function useEnergyUploadSync(cartItemId: CartItemId) {
  const { value, patch } = useEnergyCertificateConfig(cartItemId)

  const variantId = usePfStore((s) => s.items.find((i) => i.id === cartItemId)?.variantId ?? null)
  const exterior = useLineDocScope(useExteriorViewUpload(), cartItemId, variantId)
  const nameplate = useLineDocScope(useHeaterNameplateUpload(), cartItemId, variantId)
  const buildingShape = useLineDocScope(useBuildingShapeUpload(), cartItemId, variantId)
  const construction = useLineDocScope(useConstructionDescUpload(), cartItemId, variantId)

  const dropped = useSessionDocumentsDropped(cartItemId, 'energy_certificate_of_need')

  const exteriorGate = useGatedUploadScope(exterior, exterior.filenames, value.exteriorViewFilenames,
    (filenames) => patch({ exteriorViewFilenames: filenames }))
  const nameplateGate = useGatedUploadScope(nameplate, nameplate.filenames, value.heaterNameplateFilenames,
    (filenames) => patch({ heaterNameplateFilenames: filenames }))
  const buildingShapeGate = useGatedUploadScope(buildingShape, buildingShape.filenames, value.buildingShapeFilenames,
    (filenames) => patch({ buildingShapeFilenames: filenames }))
  const constructionGate = useGatedUploadScope(construction, construction.filenames, value.constructionDescriptionFilenames,
    (filenames) => patch({ constructionDescriptionFilenames: filenames }))

  return {
    hasRestoredFiles: dropped && !(exteriorGate.touched || nameplateGate.touched || buildingShapeGate.touched || constructionGate.touched),
    exterior,
    nameplate,
    buildingShape,
    construction,
    uploadExterior: exteriorGate.upload,
    deleteExterior: exteriorGate.remove,
    uploadNameplate: nameplateGate.upload,
    deleteNameplate: nameplateGate.remove,
    uploadBuildingShape: buildingShapeGate.upload,
    deleteBuildingShape: buildingShapeGate.remove,
    uploadConstruction: constructionGate.upload,
    deleteConstruction: constructionGate.remove,
  }
}
